import { format } from 'erisxkit/client';
import ReactTable from 'react-table-6';
import React from 'react';
import moment from 'moment';
import { NavLink } from 'react-router-dom';
import get from 'lodash/get';
import { formatUSD } from '../utils/methods';
import compareNumbers from '../utils/agGrid/comparators/compareNumbers';

const assetBalances = (showCurrenexBalances, cdfEnabled = false) => [
  {
    Header: 'Account Balances',
    columns: [
      {
        Header: 'Account Label',
        expander: true,
        resizable: true,
        Expander: (row) => {
          // Only show row expander when the row has _subRows
          if (get(row, ['original', '_subRows', 'length'], 0)) {
            return (
              <div className={`rt-cell-${row.level}`}>
                <ReactTable.defaultProps.ExpanderComponent {...row} />
                <NavLink
                  target="_blank"
                  className="mono"
                  to={`/balances/${get(row, ['original', 'accountId'])}#requests`}
                >
                  {get(row, ['original', 'accountLabel'])}
                </NavLink>
              </div>
            );
          }
          return (
            <div className={`rt-cell-${row.level}`}>
              <NavLink
                target="_blank"
                className="mono"
                to={`/balances/${get(row, ['original', 'accountId'])}#requests`}
              >
                {get(row, ['original', 'accountLabel'])}
              </NavLink>
            </div>
          );
        },
      },
      {
        Header: 'Asset (FD)',
        id: 'asset',
        accessor: (row) =>
          row.symbol ? `${row.symbol.toUpperCase()} (${row.fd})` : '',
        className: 'text-right',
        headerClassName: 'text-right',
      },

      {
        Header: 'Opening',
        accessor: (row) => row.openingBalance || 0,
        Cell: ({ row }) => format(row.openingBalance) || 0,
        id: 'openingBalance',
        className: 'text-right mono',
        headerClassName: 'text-right',
        sortMethod: compareNumbers,
      },
      {
        Header: 'Spot Movements',
        accessor: (row) => row.spotMovement || 0,
        id: 'spotMovement',
        className: 'text-right mono',
        headerClassName: 'text-right',
        sortMethod: compareNumbers,
      },
      {
        Header: 'Ex Fees',
        accessor: (row) => row.exchangeFees,
        Cell: ({ row }) => format(row.exchangeFees) || 0,

        id: 'exchangeFees',
        className: 'text-right mono',
        headerClassName: 'text-right',
        sortMethod: compareNumbers,
      },
      {
        Header: 'Cl Fees',
        accessor: (row) => row.clearingFees,
        Cell: ({ row }) => format(row.clearingFees) || 0,
        id: 'clearingFees',
        className: 'text-right mono',
        headerClassName: 'text-right',
        sortMethod: compareNumbers,
      },
      {
        Header: 'Other Fees',
        accessor: (row) => row.otherFees || 0,
        Cell: ({ row }) => format(row.otherFees) || 0,
        id: 'otherFees',
        className: 'text-right mono',
        headerClassName: 'text-right',
        sortMethod: compareNumbers,
      },
      {
        Header: 'Asset Movements',
        accessor: (row) => row.assetMovement,
        Cell: ({ row }) => row.assetMovement || 0,
        id: 'assetMovement',
        className: 'text-right mono',
        headerClassName: 'text-right',
        sortMethod: compareNumbers,
      },
      {
        Header: 'Realized P&L',
        accessor: (row) => row.realizedPAndL || 0,
        id: 'realizedPAndL',
        className: 'text-right mono',
        headerClassName: 'text-right',
        sortMethod: compareNumbers,
      },
      {
        Header: 'Futures Delivery',
        accessor: (row) => row.futuresDelivery || 0,
        id: 'futuresDelivery',
        className: 'text-right mono',
        headerClassName: 'text-right',
        sortMethod: compareNumbers,
      },
      {
        Header: 'Variation Margin',
        accessor: (row) => row.variationMargin || 0,
        Cell: ({ row }) => format(row.variationMargin) || 0,
        id: 'variationMargin',
        className: 'text-right mono',
        headerClassName: 'text-right',
        sortMethod: compareNumbers,
      },
      ...(cdfEnabled
        ? [
            {
              Header: 'Funding Amount',
              accessor: (row) => row.fundingAmount,
              Cell: ({ row }) => format(row.fundingAmount) || '-',
              id: 'fundingAmount',
              className: 'text-right mono',
              headerClassName: 'text-right',
              sortMethod: compareNumbers,
            },
          ]
        : []),
      {
        Header: 'Unsettled NOV',
        accessor: (row) => row.unsettledNov || '',
        Cell: ({ row }) => format(row.unsettledNov) || '',
        id: 'unsettledNov',
        className: 'text-right mono',
        headerClassName: 'text-right',
        sortMethod: compareNumbers,
      },
      {
        Header: 'Expiry Cash Movements',
        accessor: (row) => row.exercisedValue || '',
        Cell: ({ row }) => format(row.exercisedValue) || '',
        id: 'exercisedValue',
        className: 'text-right mono',
        headerClassName: 'text-right',
        sortMethod: compareNumbers,
      },
      {
        Header: 'Closing',
        accessor: (row) => row.closingBalance || 0,
        Cell: ({ row }) => format(row.closingBalance) || 0,
        id: 'closingBalance',
        className: 'text-right mono',
        headerClassName: 'text-right',
        sortMethod: compareNumbers,
      },
      {
        Header: 'Total Equity',
        accessor: (row) => row.totalEquity || 0,
        Cell: ({ row }) => format(row.totalEquity) || 0,
        id: 'totalEquity',
        className: 'text-right mono',
        headerClassName: 'text-right',
        sortMethod: compareNumbers,
      },
      {
        Header: 'TME Balance',
        accessor: (row) => format(row.currenexBalance) || 0,
        id: 'tmeBalance',
        className: 'text-right mono',
        headerClassName: 'text-right',
        sortMethod: compareNumbers,
      },
      {
        Header: 'TME Available Balance',
        accessor: (row) => row.purchasingPower || 0,
        Cell: ({ row }) => format(row.purchasingPower) || 0,
        id: 'purchasingPower',
        className: 'text-right mono',
        headerClassName: 'text-right',
        show: showCurrenexBalances,
        sortMethod: compareNumbers,
      },
      {
        Header: 'Initial Margin',
        accessor: (row) => row.initialMargin || 0,
        Cell: ({ row }) => format(row.initialMargin) || 0,
        id: 'initialMargin',
        className: 'text-right mono',
        headerClassName: 'text-right',
        sortMethod: compareNumbers,
      },
      {
        Header: 'Maintenance Margin',
        accessor: (row) => row.maintenanceMargin || 0,
        Cell: ({ row }) => format(row.maintenanceMargin) || 0,
        id: 'maintenanceMargin',
        className: 'text-right mono',
        headerClassName: 'text-right',
        sortMethod: compareNumbers,
      },
      {
        Header: 'Pending',
        accessor: (row) => format(row.pendingBalance) || '',
        id: 'pending',
        className: 'text-right mono',
        headerClassName: 'text-right',
        sortMethod: compareNumbers,
      },
      {
        Header: 'Total USD value',
        accessor: (row) => row.usdValue || '',
        Cell: ({ row }) => (row?.usdValue ? formatUSD(row.usdValue) : '-'),
        id: 'usdValue',
        className: 'text-right mono',
        headerClassName: 'text-right',
        width: 160,
        sortMethod: compareNumbers,
      },
      {
        Header: 'Total Excess/Deficit',
        accessor: (row) => row.totalExcessDeficit || 0,
        Cell: ({ row }) => format(row.totalExcessDeficit) || 0,
        id: 'totalExcessDeficit',
        className: 'text-right mono',
        headerClassName: 'text-right',
        width: 160,
        sortMethod: compareNumbers,
      },
      {
        Header: 'Settlement Balance',
        accessor: (row) => {
          const settlementBalance = get(row, [
            'settlementDetail',
            'settlementBalance',
          ]);
          return settlementBalance || '-';
        },
        id: 'settlementBalance',
        className: 'text-right mono',
        headerClassName: 'text-right',
        width: 150,
        sortMethod: compareNumbers,
      },
      {
        Header: 'Settlement Time',
        accessor: (row) => {
          const settlementTime = get(row, [
            'settlementDetail',
            'settlementTime',
          ]);
          return settlementTime
            ? moment(settlementTime).format('YYYY-MM-DD HH:mm')
            : '-';
        },
        id: 'settlementTime',
        className: 'text-right mono',
        headerClassName: 'text-right',
        width: 190,
      },
    ],
  },
];

export default assetBalances;
